import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, LogOut, Package, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { Field, PageHead } from "@/components/common";
import { Switch } from "@/components/ui/switch";

function ProfileForm({ user, onSaved }) {
  const [f, setF] = useState({ full_name: user.full_name || "", mobile: user.mobile || "", email: user.email || "", birthdate: user.birthdate || "", address: user.address || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/customer/profile", { ...f, email: f.email || null, birthdate: f.birthdate || null, address: f.address || null }); toast.success("Profile saved"); onSaved(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="surface space-y-3 p-5" data-testid="profile-form">
      <Field label="Full name"><input className="field" value={f.full_name} onChange={set("full_name")} data-testid="profile-name-input" /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Mobile"><input className="field" value={f.mobile} onChange={set("mobile")} data-testid="profile-mobile-input" /></Field>
        <Field label="Birthdate"><input className="field" type="date" value={f.birthdate} onChange={set("birthdate")} data-testid="profile-birthdate-input" /></Field>
      </div>
      <Field label="Email (optional)"><input className="field" type="email" value={f.email} onChange={set("email")} data-testid="profile-email-input" /></Field>
      <Field label="Address"><input className="field" value={f.address} onChange={set("address")} data-testid="profile-address-input" /></Field>
      <button className="btn-primary w-full" data-testid="profile-save-button">Save profile</button>
    </form>
  );
}

function Family({ user, onChange }) {
  const [f, setF] = useState({ full_name: "", relationship: "CHILD", birthdate: "" });
  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/customer/beneficiaries", { ...f, birthdate: f.birthdate || null }); setF({ full_name: "", relationship: "CHILD", birthdate: "" }); toast.success("Family member added"); onChange(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/customer/beneficiaries/${id}`); onChange(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="surface space-y-3 p-5" data-testid="family-section">
      <h2 className="text-base font-bold">Family members</h2>
      <p className="text-xs text-slate-500">Eligible family members (parents, spouse, children) can use benefits that allow them, such as checkups.</p>
      {(user.beneficiaries || []).map((b) => (
        <div key={b.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2.5 text-sm" data-testid={`beneficiary-${b.id}`}>
          <span><span className="font-semibold">{b.full_name}</span> <span className="text-xs capitalize text-slate-500">· {b.relationship.toLowerCase()}</span></span>
          <button className="p-1 text-slate-400 hover:text-red-600" onClick={() => remove(b.id)} data-testid={`remove-beneficiary-${b.id}`}><Trash2 size={15} /></button>
        </div>
      ))}
      <form onSubmit={add} className="space-y-2">
        <input className="field" placeholder="Full name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} required data-testid="beneficiary-name-input" />
        <div className="grid grid-cols-2 gap-2">
          <select className="field" value={f.relationship} onChange={(e) => setF({ ...f, relationship: e.target.value })} data-testid="beneficiary-relationship-select">
            <option value="CHILD">Child</option><option value="SPOUSE">Spouse</option><option value="PARENT">Parent</option>
          </select>
          <input className="field" type="date" value={f.birthdate} onChange={(e) => setF({ ...f, birthdate: e.target.value })} data-testid="beneficiary-birthdate-input" />
        </div>
        <button className="btn-secondary w-full" data-testid="add-beneficiary-button"><UserPlus size={15} /> Add family member</button>
      </form>
    </section>
  );
}

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const toggleConsent = async (accepted) => {
    try { await api.post("/customer/consent", { accepted }); await refresh(); toast.success(accepted ? "Consent recorded" : "Consent withdrawn"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-5">
      <PageHead eyebrow="Profile" title={user.full_name} sub={<span className="font-mono">{user.username}</span>} />
      <section className="surface space-y-2 p-5" data-testid="consent-section">
        <div className="flex items-center justify-between gap-3">
          <div><p className="text-sm font-bold">Privacy & data consent</p>
            <p className="text-xs text-slate-500">Allow GIA and assigned providers to process my health information to deliver package services (Data Privacy Act of 2012).</p></div>
          <Switch checked={!!user.consent} onCheckedChange={toggleConsent} data-testid="consent-switch" />
        </div>
        {user.consent && <p className="text-[11px] text-emerald-700">Accepted {fmtDateTime(user.consent.accepted_at)}</p>}
      </section>
      <ProfileForm user={user} onSaved={refresh} />
      <Family user={user} onChange={refresh} />
      <div className="grid grid-cols-2 gap-2">
        <Link to="/app/packages" className="btn-secondary" data-testid="profile-my-packages"><Package size={15} /> My packages</Link>
        <Link to="/change-password" className="btn-secondary" data-testid="profile-change-password"><KeyRound size={15} /> Password</Link>
      </div>
      <button className="btn-danger w-full" onClick={async () => { await logout(); nav("/login"); }} data-testid="customer-logout-button"><LogOut size={15} /> Sign out</button>
    </div>
  );
}
